import { graphql } from 'react-apollo'

import { branch, compose, lifecycle, renderComponent, withHandlers, withState } from 'recompose'

import CenteredSpinner from 'components/CenteredSpinner'
import withPartnerProps, { WithPartnerProps } from 'components/HOCs/WithPartnerProps'
import { Portfolio, RoundUpProfile, User } from 'generated/types'
import trackEvent from 'utils/analytics/trackEvent'
import { withApolloErrorModal } from 'utils/Apollo'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { isDefaultFlow } from 'utils/FlowProvider/utils'
import { getGeneralError } from 'utils/getGeneralError'
import { WithOptimizelyProps, withOptimizely } from 'utils/Optimizely'

import recommendedPortfolioAndFundingSourceQuery from './gql/recommendedPortfolio.query.gql'
import registrationCompleteMutation from './gql/registrationComplete.mutation.gql'

type GqlProps = {
  loading: boolean
  portfolios: Portfolio[]
  user: User
  roundUpProfile: RoundUpProfile
}

type Etf = {
  amount: number
  name: string
}

export type Props = {
  etfs: Etf[]
  userPortfolio: Portfolio
  handleClick: () => void
  isSubmitting: boolean
  setIsSubmitting: (value: boolean) => void
  loading: boolean
  portfolioName: string
  values: number[]
} & WithFlowRouterProps &
  WithPartnerProps &
  WithOptimizelyProps

export const enhance = compose<Props, {}>(
  withFlowRouter,
  withPartnerProps,
  withOptimizely,
  withApolloErrorModal,
  withState('isSubmitting', 'setIsSubmitting', false),
  graphql(registrationCompleteMutation, { name: 'registrationComplete' }),
  lifecycle<Props, {}>({
    componentDidMount() {
      if (isDefaultFlow()) {
        if (['core', 'later', 'tiered'].includes(this.props.flow)) {
          trackEvent('Funnel Complete', {
            eventData: {
              event: 'funnel_complete',
              event_text: 'Funnel Complete',
            },
            flow: this.props.flow,
            partner: this.props.partner,
          })
        }
      }
    },
  }),
  graphql<{}, GqlProps, {}, {}>(recommendedPortfolioAndFundingSourceQuery, {
    options: { fetchPolicy: 'network-only' },
    props: ({ data }) => {
      if (data.loading) {
        return {
          etfs: [],
          loading: true,
        }
      } else {
        const portfolioName = data.user.portfolio?.name
        const userPortfolioId = data.user.portfolio?.id
        let etfs =
          data.portfolios?.filter(portfolio => portfolio.id === userPortfolioId)[0]?.etfs ?? []

        const userPortfolio =
          data.portfolios?.filter(portfolio => portfolio.id === userPortfolioId)?.[0] ?? null

        // Sort the etfs from highest to lowest
        etfs = etfs.sort((etf1, etf2) => etf2.amount - etf1.amount)
        const values = etfs.map(etf => etf.amount)
        const fundingSource = !!data.user?.fundingSource?.type ?? false
        const roundUpsEnabled = !!data.roundUpProfile?.roundUpsEnabled ?? false

        return {
          etfs,
          loading: false,
          portfolioName,
          values,
          fundingSource,
          roundUpsEnabled,
          userPortfolio,
        }
      }
    },
  }),
  withHandlers({
    handleClick: ({
      route,
      fundingSource,
      roundUpsEnabled,
      setIsSubmitting,
      registrationComplete,
      trackOptimizely,
      closeErrorModal,
      showErrorModal,
    }) => () => {
      setIsSubmitting(true)
      const routeUser = () => {
        route({
          from: FlowRoute.PortfolioOverview,
          action: 'continue',
          state: { fundingSource, roundUpsEnabled },
        })
      }

      const handleDefaultFlowClick = () => {
        registrationComplete()
          .then(() => {
            trackOptimizely('registration.complete')
            return routeUser()
          })
          .catch(error => {
            setIsSubmitting(false)

            const retryHandler = () => {
              closeErrorModal()
              handleDefaultFlowClick()
            }
            const { title, message, actions, image } = getGeneralError(
              error,
              { closeErrorModal },
              retryHandler,
            )
            showErrorModal({ title, message, actions, image })
          })
      }

      if (isDefaultFlow()) {
        handleDefaultFlowClick()
      } else {
        return routeUser()
      }
    },
  }),
  branch(({ loading }) => loading, renderComponent(CenteredSpinner)),
)
